import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import {
  XIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Loader2Icon,
  TrendingUpIcon,
} from 'lucide-react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  Cell,
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TR_MONTHS = [
  'Ocak','Şubat','Mart','Nisan','Mayıs','Haziran',
  'Temmuz','Ağustos','Eylül','Ekim','Kasım','Aralık',
];

const fmtTRY = (n) =>
  new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 2 }).format(Number(n || 0));

/**
 * SupplierAnnualChart — modal that shows a full-year bar chart of monthly
 * totals for ONE supplier. The year picker lets the user navigate forward /
 * backward; we re-fetch /api/solo/suppliers/monthly?year=YYYY on each change.
 *
 * Bars are pure CSS (height: percent of peak) — no chart library dependency.
 * Empty months still render as faint placeholder rectangles so the user can
 * count the bars even when only a few months are populated.
 *
 * Props:
 *   supplier        — the supplier row { id, name, ... }
 *   onClose()       — close handler
 *   initialYear     — defaults to the current calendar year
 */
const SupplierAnnualChart = ({ supplier, onClose, initialYear }) => {
  const [year, setYear] = useState(initialYear || new Date().getFullYear());
  const [months, setMonths] = useState(new Array(12).fill(0));
  const [prevMonths, setPrevMonths] = useState(new Array(12).fill(0));
  const [prevYearTotal, setPrevYearTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  // Grafik türü — kullanıcı bar/line arasında toggle edebilir.
  // `compare` aç/kapa: bir önceki yıl (year-1) verisini üst üste bindirir.
  const [chartType, setChartType] = useState('bar');
  const [compareYoy, setCompareYoy] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const norm = (supplier?.name || '').trim().toLocaleLowerCase('tr-TR');
        // Hem şu anki hem de bir önceki yılın aylık verisini paralel çek
        // → grafiğin YoY karşılaştırma modu için ikisi de gerekli.
        const [rCur, rPrev] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/solo/suppliers/monthly?year=${year}`,
            { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${BACKEND_URL}/api/solo/suppliers/monthly?year=${year - 1}`,
            { headers: { Authorization: `Bearer ${token}` } })
            .catch(() => ({ data: { suppliers: [] } })),
        ]);
        if (cancelled) return;
        const findRow = (data) => (data?.suppliers || []).find(
          (s) => (s.name || '').trim().toLocaleLowerCase('tr-TR') === norm || s.key === norm
        );
        const cur = findRow(rCur.data);
        const prev = findRow(rPrev.data);
        setMonths(cur?.months || new Array(12).fill(0));
        setPrevMonths(prev?.months || new Array(12).fill(0));
        setPrevYearTotal(Number(cur?.prev_year_total || (prev?.months || []).reduce((a, b) => a + Number(b || 0), 0) || 0));
      } catch (e) {
        if (!cancelled) {
          setMonths(new Array(12).fill(0));
          setPrevMonths(new Array(12).fill(0));
          setPrevYearTotal(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [year, supplier]);

  const total = months.reduce((s, m) => s + (Number(m) || 0), 0);
  const peak = Math.max(...months, 0);

  // Year-over-year delta — only meaningful when both years had activity.
  const yoyPct = prevYearTotal > 0 ? ((total - prevYearTotal) / prevYearTotal) * 100 : null;
  const yoyDelta = total - prevYearTotal;
  const yoyUp = yoyPct != null && yoyPct > 0;
  const yoyDown = yoyPct != null && yoyPct < 0;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="supplier-annual-chart"
    >
      <div
        className="w-full max-w-3xl bg-white rounded-xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
              <TrendingUpIcon className="w-5 h-5 text-purple-700" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-gray-900 truncate">
                {supplier?.name} — Yıllık Trend
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Aylık harcama dağılımı · Yıl toplamı:{' '}
                <strong className="text-gray-700">{fmtTRY(total)}</strong>
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 flex-shrink-0"
            data-testid="supplier-annual-close"
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Year picker */}
        <div className="px-5 py-3 border-b bg-gray-50 flex items-center justify-between">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setYear((y) => y - 1)}
            data-testid="supplier-annual-prev-year"
          >
            <ChevronLeftIcon className="w-4 h-4 mr-1" /> {year - 1}
          </Button>
          <div
            className="text-base font-semibold text-gray-900"
            data-testid="supplier-annual-current-year"
          >
            {year}
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setYear((y) => y + 1)}
            data-testid="supplier-annual-next-year"
          >
            {year + 1} <ChevronRightIcon className="w-4 h-4 ml-1" />
          </Button>
        </div>

        {/* Chart */}
        <div className="p-5">
          {loading ? (
            <div className="h-64 flex items-center justify-center text-gray-400 gap-2">
              <Loader2Icon className="w-4 h-4 animate-spin" /> Yükleniyor...
            </div>
          ) : total === 0 ? (
            <div className="h-64 flex flex-col items-center justify-center text-gray-400">
              <span className="text-3xl mb-2">📉</span>
              <div className="text-sm">{year} yılında bu firma için kayıt yok.</div>
            </div>
          ) : (
            <>
              {/* Year-over-year banner */}
              {yoyPct != null && (
                <div
                  className={`mb-4 px-3 py-2 rounded-lg flex items-center gap-2 text-sm ${
                    yoyUp
                      ? 'bg-rose-50 text-rose-800 border border-rose-200'
                      : yoyDown
                      ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                      : 'bg-gray-50 text-gray-700 border border-gray-200'
                  }`}
                  data-testid="supplier-yoy-banner"
                >
                  <span className="text-lg">
                    {yoyUp ? '↑' : yoyDown ? '↓' : '→'}
                  </span>
                  <span className="flex-1">
                    {year - 1} yılına göre{' '}
                    <strong>{Math.abs(yoyPct).toFixed(0)}%</strong>{' '}
                    {yoyUp ? 'artış' : yoyDown ? 'düşüş' : 'değişim yok'} —{' '}
                    {year - 1}: {fmtTRY(prevYearTotal)} · {year}: {fmtTRY(total)}
                    {' '}({yoyDelta >= 0 ? '+' : ''}{fmtTRY(yoyDelta)})
                  </span>
                </div>
              )}

              {/* Grafik türü + karşılaştırma toggle'ı */}
              <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5" data-testid="supplier-chart-type-toggle">
                  <button
                    type="button"
                    onClick={() => setChartType('bar')}
                    className={`px-3 py-1 rounded-md text-xs font-medium transition ${
                      chartType === 'bar' ? 'bg-purple-600 text-white' : 'text-gray-600 hover:bg-gray-50'
                    }`}
                    data-testid="supplier-chart-type-bar"
                  >
                    Bar
                  </button>
                  <button
                    type="button"
                    onClick={() => setChartType('line')}
                    className={`px-3 py-1 rounded-md text-xs font-medium transition ${
                      chartType === 'line' ? 'bg-purple-600 text-white' : 'text-gray-600 hover:bg-gray-50'
                    }`}
                    data-testid="supplier-chart-type-line"
                  >
                    Çizgi
                  </button>
                </div>
                <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={compareYoy}
                    onChange={(e) => setCompareYoy(e.target.checked)}
                    className="w-3.5 h-3.5 rounded text-purple-600"
                    data-testid="supplier-chart-compare-toggle"
                  />
                  {year - 1} ile karşılaştır
                </label>
              </div>

              {/* Recharts grafiği — bar veya line + isteğe bağlı YoY overlay */}
              <div className="h-72 w-full" data-testid="supplier-annual-chart-body">
                <ResponsiveContainer width="100%" height="100%">
                  {chartType === 'bar' ? (
                    <BarChart
                      data={months.map((v, i) => ({
                        month: TR_MONTHS[i].slice(0, 3),
                        full: TR_MONTHS[i],
                        [String(year)]: Number(v || 0),
                        [String(year - 1)]: Number(prevMonths[i] || 0),
                      }))}
                      margin={{ top: 20, right: 12, left: 0, bottom: 4 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                      <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#6b7280' }}
                        axisLine={{ stroke: '#e5e7eb' }} tickLine={false} />
                      <YAxis tickFormatter={(v) => v >= 1000 ? `${Math.round(v / 1000)}K` : v}
                        tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={false}
                        tickLine={false} width={45} />
                      <Tooltip
                        formatter={(v) => fmtTRY(v)}
                        labelFormatter={(_, payload) => payload?.[0]?.payload?.full || ''}
                        contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb',
                          boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', fontSize: 12 }}
                        cursor={{ fill: 'rgba(147, 51, 234, 0.08)' }}
                      />
                      {compareYoy && <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />}
                      {compareYoy && (
                        <Bar dataKey={String(year - 1)} fill="#d8b4fe" radius={[6, 6, 0, 0]} maxBarSize={24} />
                      )}
                      <Bar dataKey={String(year)} fill="#a855f7" radius={[6, 6, 0, 0]} maxBarSize={24}>
                        {!compareYoy && months.map((v, i) => (
                          <Cell key={i} fill={v > 0 ? '#a855f7' : '#f3f4f6'} />
                        ))}
                      </Bar>
                    </BarChart>
                  ) : (
                    <LineChart
                      data={months.map((v, i) => ({
                        month: TR_MONTHS[i].slice(0, 3),
                        full: TR_MONTHS[i],
                        [String(year)]: Number(v || 0),
                        [String(year - 1)]: Number(prevMonths[i] || 0),
                      }))}
                      margin={{ top: 20, right: 12, left: 0, bottom: 4 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                      <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#6b7280' }}
                        axisLine={{ stroke: '#e5e7eb' }} tickLine={false} />
                      <YAxis tickFormatter={(v) => v >= 1000 ? `${Math.round(v / 1000)}K` : v}
                        tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={false}
                        tickLine={false} width={45} />
                      <Tooltip
                        formatter={(v) => fmtTRY(v)}
                        labelFormatter={(_, payload) => payload?.[0]?.payload?.full || ''}
                        contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb',
                          boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', fontSize: 12 }}
                      />
                      {compareYoy && <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />}
                      {compareYoy && (
                        <Line
                          type="monotone"
                          dataKey={String(year - 1)}
                          stroke="#d8b4fe"
                          strokeWidth={2}
                          strokeDasharray="4 4"
                          dot={{ r: 3 }}
                          activeDot={{ r: 5 }}
                        />
                      )}
                      <Line
                        type="monotone"
                        dataKey={String(year)}
                        stroke="#a855f7"
                        strokeWidth={3}
                        dot={{ r: 4 }}
                        activeDot={{ r: 6 }}
                      />
                    </LineChart>
                  )}
                </ResponsiveContainer>
              </div>
              {/* Aylık toplam liste — grafiğin altında hızlı sayısal okuma
                  için (grafik + rakamlar birlikte). */}
              <div className="grid grid-cols-6 md:grid-cols-12 gap-1 mt-3 text-center">
                {months.map((v, i) => (
                  <div
                    key={i}
                    className={`text-[10px] px-1 py-0.5 rounded ${
                      v > 0 ? 'text-purple-800 bg-purple-50' : 'text-gray-300'
                    }`}
                    data-testid={`supplier-annual-value-${i + 1}`}
                    title={`${TR_MONTHS[i]} ${year}: ${fmtTRY(v)}`}
                  >
                    {v > 0 ? fmtTRY(v) : '—'}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SupplierAnnualChart;
